// src/helpers/tokenHelper.ts
// Token expiration management utilities

import { postRefreshToken } from './fakebackend_helper';

interface AuthUser {
  token: string;
  access_token?: string;
  expiresIn?: number;        // Thời gian sống (giây) từ API
  expires_in?: number;       // Alternative naming
  loginTime?: number;        // Timestamp khi login
  [key: string]: any;
}

interface JWTPayload {
  exp?: number;              // Expiration time (Unix timestamp)
  nbf?: number;              // Not before time
  iat?: number;              // Issued at time
  [key: string]: any;
}

/**
 * Decode JWT token (không verify signature, chỉ parse payload)
 * @param token JWT token string
 * @returns Decoded payload hoặc null nếu invalid
 */
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode base64url payload
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Lưu auth user vào localStorage với thời gian login
 */
export const saveAuthUser = (userData: any): void => {
  const authUser: AuthUser = {
    ...userData,
    loginTime: Date.now(), // Lưu timestamp hiện tại
  };
  
  localStorage.setItem('authUser', JSON.stringify(authUser));
};

/**
 * Lấy auth user từ localStorage
 */
export const getAuthUser = (): AuthUser | null => {
  try {
    const stored = localStorage.getItem('authUser');
    if (!stored) return null;
    
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error parsing authUser:', error);
    return null;
  }
};

/**
 * Kiểm tra token có hết hạn không
 * @returns true nếu token còn hạn, false nếu đã hết hạn
 */
export const isTokenValid = (): boolean => {
  const authUser = getAuthUser();
  
  if (!authUser) return false;
  
  const token = authUser.token || authUser.access_token;
  if (!token) return false;
  
  // Ưu tiên decode JWT để lấy exp claim
  const payload = decodeJWT(token);
  if (payload?.exp) {
    // exp là Unix timestamp (giây), so sánh với thời gian hiện tại
    const currentTimeSeconds = Math.floor(Date.now() / 1000);
    return currentTimeSeconds < payload.exp;
  }
  
  // Fallback: Kiểm tra bằng expiresIn + loginTime (backward compatibility)
  const expiresIn = authUser.expiresIn || authUser.expires_in;
  if (!expiresIn) return true; // Không có thông tin expiry
  
  const loginTime = authUser.loginTime;
  if (!loginTime) return true; // Không có loginTime
  
  const elapsedTime = Date.now() - loginTime;
  const expiresInMs = expiresIn * 1000;
  
  return elapsedTime < expiresInMs;
};

/**
 * Lấy thời gian còn lại của token (giây)
 * @returns Số giây còn lại, hoặc 0 nếu đã hết hạn, hoặc -1 nếu không có thông tin
 */
export const getTokenRemainingTime = (): number => {
  const authUser = getAuthUser();
  
  if (!authUser) return 0;
  
  const token = authUser.token || authUser.access_token;
  if (!token) return 0;
  
  // Ưu tiên decode JWT để lấy exp claim
  const payload = decodeJWT(token);
  if (payload?.exp) {
    const currentTimeSeconds = Math.floor(Date.now() / 1000);
    const remainingSeconds = payload.exp - currentTimeSeconds;
    return Math.max(0, remainingSeconds);
  }
  
  // Fallback: Tính bằng expiresIn + loginTime
  const expiresIn = authUser.expiresIn || authUser.expires_in;
  const loginTime = authUser.loginTime;
  
  if (!expiresIn || !loginTime) return -1;
  
  const elapsedTimeSeconds = Math.floor((Date.now() - loginTime) / 1000);
  const remainingSeconds = expiresIn - elapsedTimeSeconds;
  
  return Math.max(0, remainingSeconds);
};

/**
 * Clear auth data khỏi localStorage
 */
export const clearAuthUser = (): void => {
  localStorage.removeItem('authUser');
};

/**
 * Gọi API refresh token bằng refresh_token đang lưu trong localStorage, ghi đè lại authUser
 * với access_token/refresh_token mới nếu thành công.
 * @returns true nếu refresh thành công, false nếu refresh_token không có/hết hạn/bị revoke
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  const authUser = getAuthUser();
  const refreshToken = authUser?.refresh_token;

  if (!refreshToken) return false;

  try {
    // api.create() đi qua response interceptor của axios (api_helper.ts) nên response ở đây
    // chính là TokenDto (access_token/refresh_token/expires_in...), không phải AxiosResponse
    const response: any = await postRefreshToken({ refresh_token: refreshToken });
    saveAuthUser(response);
    return true;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
};

/**
 * Kiểm tra và tự động logout nếu token hết hạn
 * @param onExpired Callback khi token hết hạn
 */
export const checkTokenExpiration = (onExpired?: () => void): boolean => {
  if (!isTokenValid()) {
    clearAuthUser();
    if (onExpired) {
      onExpired();
    }
    return false;
  }
  return true;
};